import { useState, useMemo, useCallback } from 'react'
import { Link } from '@inertiajs/react'
import PublicLayout from '@/layouts/PublicLayout'
import SeoHead from '@/components/SeoHead'
import AnimatedNumber from '@/components/AnimatedNumber'
import CtaBanner from '@/components/CtaBanner'
import BorrowerCtaBanner from '@/components/BorrowerCtaBanner'
import { calculateTrueCost, type TrueCostInputs, type TrueCostResult, type CostBreakdownItem } from '@/lib/trueCostCalculations'

// ─── Default values ─────────────────────────────────────────────────────────

const DEFAULT_INPUTS: TrueCostInputs = {
  loanAmount: 200000,
  interestRate: 12,
  termMonths: 12,
  loanType: 'interest_only',
  originationPoints: 2,
  flatFees: 1500,
  holdMonths: 12,
  extensionFeePoints: 1,
  extensionPeriodMonths: 3,
  prepaymentPenalty: 0,
}

// ─── Page component ─────────────────────────────────────────────────────────

export default function TrueCostCalculatorPage() {
  const [inputs, setInputs] = useState<TrueCostInputs>(DEFAULT_INPUTS)
  const [compareEnabled, setCompareEnabled] = useState(false)
  const [compareInputs, setCompareInputs] = useState<TrueCostInputs>({
    ...DEFAULT_INPUTS,
    interestRate: 10,
    originationPoints: 3,
    flatFees: 2000,
  })

  // Project overrun slider state — shared between both loans
  const [overrunMonths, setOverrunMonths] = useState(inputs.holdMonths)

  const clamp = useCallback((val: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, val))
  }, [])

  const updateInput = useCallback(<K extends keyof TrueCostInputs>(key: K, value: TrueCostInputs[K]) => {
    setInputs((prev) => ({ ...prev, [key]: value }))
  }, [])

  const updateCompareInput = useCallback(<K extends keyof TrueCostInputs>(key: K, value: TrueCostInputs[K]) => {
    setCompareInputs((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Compute results with overrun slider applied
  const resultA = useMemo(
    () => calculateTrueCost({ ...inputs, holdMonths: overrunMonths }),
    [inputs, overrunMonths],
  )

  const resultB = useMemo(
    () => compareEnabled ? calculateTrueCost({ ...compareInputs, holdMonths: overrunMonths }) : null,
    [compareInputs, compareEnabled, overrunMonths],
  )

  // Base result (at expected hold, not slider) for delta display
  const baseResultA = useMemo(
    () => calculateTrueCost(inputs),
    [inputs],
  )

  const overrunExtraCost = resultA.totalCost - baseResultA.totalCost

  // Slider range
  const sliderMin = inputs.termMonths
  const sliderMax = inputs.termMonths + 18

  // Color for overrun indicator
  const overrunRatio = inputs.termMonths > 0
    ? (overrunMonths - inputs.termMonths) / 18
    : 0
  const overrunColor = overrunRatio <= 0
    ? 'text-emerald-600'
    : overrunRatio <= 0.33
      ? 'text-yellow-600'
      : overrunRatio <= 0.66
        ? 'text-amber-600'
        : 'text-red-600'

  const overrunBg = overrunRatio <= 0
    ? 'bg-emerald-50 border-emerald-200'
    : overrunRatio <= 0.33
      ? 'bg-yellow-50 border-yellow-200'
      : overrunRatio <= 0.66
        ? 'bg-amber-50 border-amber-200'
        : 'bg-red-50 border-red-200'

  return (
    <PublicLayout
      title="True Cost of Borrowing Calculator"
      description="Calculate the real cost of a private money loan — including points, fees, and extension penalties."
    >
      <SeoHead
        title="True Cost of Borrowing Calculator — Private Loan Cost Calculator | LendSolo"
        description="Calculate the real cost of a private money loan. Factor in points, fees, interest, and extension costs to compare lenders and make smarter borrowing decisions."
        canonicalUrl="https://lendsolo.com/tools/true-cost-calculator"
        schema={{
          '@type': 'WebApplication',
          name: 'True Cost of Borrowing Calculator',
          description: 'Calculate the real cost of a private money loan. Factor in points, fees, interest, and extension costs to compare lenders and make smarter borrowing decisions.',
          url: 'https://lendsolo.com/tools/true-cost-calculator',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Any',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          provider: { '@type': 'Organization', name: 'LendSolo' },
        }}
      />

      <div className={`grid ${compareEnabled ? 'lg:grid-cols-[1fr_1fr]' : 'lg:grid-cols-[400px_1fr]'} gap-8`}>
        {/* ── Loan A Inputs ── */}
        <div className="space-y-6">
          <LoanInputPanel
            title={compareEnabled ? 'Loan A' : 'Loan Details'}
            inputs={inputs}
            onUpdate={updateInput}
            clamp={clamp}
          />

          {!compareEnabled && (
            <button
              onClick={() => setCompareEnabled(true)}
              className="w-full py-3 px-4 rounded-lg border-2 border-dashed border-gray-300 text-sm font-medium text-gray-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
            >
              + Compare another loan
            </button>
          )}

          {/* Cross-link */}
          <div className="px-1">
            <Link
              href="/tools/interest-only-calculator"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1.5"
            >
              Need a detailed payment schedule? Try the interest-only calculator
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>

        {/* ── Loan B Inputs (compare mode) ── */}
        {compareEnabled && (
          <div className="space-y-6">
            <LoanInputPanel
              title="Loan B"
              inputs={compareInputs}
              onUpdate={updateCompareInput}
              clamp={clamp}
            />
            <button
              onClick={() => setCompareEnabled(false)}
              className="w-full py-2.5 px-4 rounded-lg border border-gray-200 text-sm font-medium text-gray-500 hover:text-red-600 hover:border-red-300 transition-colors"
            >
              Remove comparison
            </button>
          </div>
        )}
      </div>

      {/* ── Results ── */}
      <div className="mt-8 space-y-6">
        {/* Primary metrics */}
        {compareEnabled && resultB ? (
          <CompareResults resultA={resultA} resultB={resultB} />
        ) : (
          <SingleResults result={resultA} />
        )}

        {/* Cost breakdown */}
        <div className={`grid ${compareEnabled ? 'sm:grid-cols-2' : ''} gap-6`}>
          <CostBreakdown
            label={compareEnabled ? 'Loan A Breakdown' : 'Cost Breakdown'}
            breakdown={resultA.breakdown}
            totalCost={resultA.totalCost}
          />
          {compareEnabled && resultB && (
            <CostBreakdown
              label="Loan B Breakdown"
              breakdown={resultB.breakdown}
              totalCost={resultB.totalCost}
            />
          )}
        </div>

        {/* ── Project Overrun Slider ── */}
        <div className={`rounded-2xl shadow-sm border p-6 sm:p-8 ${overrunBg}`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">What if your project runs long?</h3>
          <p className="text-sm text-gray-500 mb-5">
            Drag the slider to see how costs change if you hold past the original loan term.
          </p>

          <div className="space-y-3">
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              value={overrunMonths}
              onChange={(e) => setOverrunMonths(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-emerald-600"
            />

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{sliderMin} mo (term)</span>
              <span className={`font-semibold ${overrunColor}`}>
                Hold: {overrunMonths} months
              </span>
              <span className="text-gray-500">{sliderMax} mo</span>
            </div>

            {overrunMonths > inputs.termMonths && (
              <div className={`mt-2 p-3 rounded-lg bg-white/60`}>
                <p className={`text-sm font-semibold ${overrunColor}`}>
                  +${overrunExtraCost.toLocaleString('en-US', { minimumFractionDigits: 2 })} in extra costs
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Effective rate: {resultA.effectiveAnnualRate.toFixed(2)}% (was {baseResultA.effectiveAnnualRate.toFixed(2)}%)
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Educational Content ── */}
      <div className="mt-12 space-y-10">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Why your interest rate doesn&apos;t tell the whole story
          </h2>

          <div className="prose prose-gray text-gray-600 space-y-4">
            <p>
              When comparing private money loans, most borrowers focus on the interest rate. But private
              loans have layered costs that go well beyond the headline number: origination points, flat
              fees for document preparation and underwriting, per-diem interest accrual, and penalties
              if the project takes longer than planned.
            </p>
            <p>
              Consider this: a 10% loan with 3 origination points actually costs more than a 12% loan
              with 0 points if you only hold the property for six months. The points are paid upfront
              regardless of how long you keep the loan, so on shorter holds they represent a larger
              share of your total cost.
            </p>
            <p>
              Project delays are the norm in real estate rehab, not the exception. Permit timelines slip,
              contractors fall behind, and market conditions shift. When your loan term expires and you
              need an extension, the fees can add thousands in unexpected costs. Understanding the
              extension math before you close is critical.
            </p>
            <p>
              This calculator helps you see the true, all-in cost of borrowing — so you can compare
              lenders on an apples-to-apples basis and make a smarter decision for your deal.
            </p>
          </div>
        </div>

        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            How to use this calculator
          </h2>

          <div className="prose prose-gray text-gray-600 space-y-4">
            <p>
              <strong>Loan Amount</strong> — The total amount you&apos;re borrowing. For fix-and-flip deals,
              this often includes both the purchase price and rehab budget if your lender finances both.
            </p>
            <p>
              <strong>Interest Rate &amp; Loan Type</strong> — Your annual interest rate and whether payments
              are interest-only (most common in private lending) or fully amortizing. Interest-only loans
              have lower monthly payments but a balloon payment at maturity.
            </p>
            <p>
              <strong>Origination Points &amp; Flat Fees</strong> — Points are a percentage of the loan amount
              charged at closing. Flat fees cover document preparation, underwriting, inspection, and other
              lender costs. Both are paid upfront and add to your total cost.
            </p>
            <p>
              <strong>Expected Hold Period</strong> — How long you plan to keep the loan. This may be shorter
              than the loan term (if you plan to sell or refinance early) or longer (if the project runs late).
              Use the overrun slider below the results to model delay scenarios.
            </p>
            <p>
              <strong>Extension &amp; Prepayment</strong> — If your project runs past the loan term, most lenders
              charge an extension fee (typically 1% of the loan amount per extension period). If you pay off
              early, some lenders charge a prepayment penalty. Enter both to see the full picture.
            </p>
          </div>
        </div>
      </div>

      <CtaBanner
        headline="Managing private loans from the other side?"
        buttonText="Try LendSolo free"
      />
      <BorrowerCtaBanner />
    </PublicLayout>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function LoanInputPanel({
  title,
  inputs,
  onUpdate,
  clamp,
}: {
  title: string
  inputs: TrueCostInputs
  onUpdate: <K extends keyof TrueCostInputs>(key: K, value: TrueCostInputs[K]) => void
  clamp: (val: number, min: number, max: number) => number
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 h-fit">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">{title}</h2>

      <div className="space-y-5">
        <InputField
          label="Loan Amount"
          prefix="$"
          type="number"
          value={inputs.loanAmount}
          onChange={(v) => onUpdate('loanAmount', clamp(Number(v), 10000, 10000000))}
        />

        <InputField
          label="Interest Rate"
          suffix="%"
          type="number"
          step="0.25"
          value={inputs.interestRate}
          onChange={(v) => onUpdate('interestRate', clamp(Number(v), 1, 30))}
        />

        <InputField
          label="Loan Term (Months)"
          type="number"
          value={inputs.termMonths}
          onChange={(v) => onUpdate('termMonths', clamp(Math.round(Number(v)), 1, 60))}
        />

        {/* Loan type toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Loan Type</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => onUpdate('loanType', 'interest_only')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                inputs.loanType === 'interest_only'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Interest-Only
            </button>
            <button
              type="button"
              onClick={() => onUpdate('loanType', 'amortizing')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                inputs.loanType === 'amortizing'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Amortizing
            </button>
          </div>
        </div>

        <InputField
          label="Origination Points"
          suffix="%"
          type="number"
          step="0.25"
          value={inputs.originationPoints}
          onChange={(v) => onUpdate('originationPoints', clamp(Number(v), 0, 10))}
        />

        <InputField
          label="Additional Flat Fees"
          prefix="$"
          type="number"
          value={inputs.flatFees}
          hint="Doc prep, underwriting, inspection, etc."
          onChange={(v) => onUpdate('flatFees', clamp(Number(v), 0, 1000000))}
        />

        <InputField
          label="Expected Hold Period (Months)"
          type="number"
          value={inputs.holdMonths}
          onChange={(v) => onUpdate('holdMonths', clamp(Math.round(Number(v)), 1, inputs.termMonths + 12))}
        />

        <InputField
          label="Extension Fee"
          suffix="%"
          type="number"
          step="0.25"
          value={inputs.extensionFeePoints}
          hint="Per extension period, if project runs past term"
          onChange={(v) => onUpdate('extensionFeePoints', clamp(Number(v), 0, 5))}
        />

        <InputField
          label="Extension Period (Months)"
          type="number"
          value={inputs.extensionPeriodMonths}
          onChange={(v) => onUpdate('extensionPeriodMonths', clamp(Math.round(Number(v)), 1, 12))}
        />

        <InputField
          label="Prepayment Penalty"
          suffix="%"
          type="number"
          step="0.25"
          value={inputs.prepaymentPenalty}
          hint="Charged if you pay off early"
          onChange={(v) => onUpdate('prepaymentPenalty', clamp(Number(v), 0, 5))}
        />
      </div>
    </div>
  )
}

function SingleResults({ result }: { result: TrueCostResult }) {
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <p className="text-sm text-gray-500 mb-1">Total Cost of Capital</p>
        <AnimatedNumber value={result.totalCost} prefix="$" className="text-3xl font-bold text-gray-900" />
        <p className="mt-1 text-xs text-gray-400">All-in borrowing cost</p>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <p className="text-sm text-gray-500 mb-1">Effective Annual Rate</p>
        <AnimatedNumber value={result.effectiveAnnualRate} suffix="%" className="text-3xl font-bold text-gray-900" />
        <p className="mt-1 text-xs text-gray-400">APR-equivalent with all fees</p>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <p className="text-sm text-gray-500 mb-1">Monthly Cost</p>
        <AnimatedNumber value={result.monthlyCost} prefix="$" className="text-3xl font-bold text-gray-900" />
        <p className="mt-1 text-xs text-gray-400">Total cost per month of use</p>
      </div>
    </div>
  )
}

function CompareResults({ resultA, resultB }: { resultA: TrueCostResult; resultB: TrueCostResult }) {
  const savings = Math.abs(resultA.totalCost - resultB.totalCost)
  const winner = resultA.totalCost <= resultB.totalCost ? 'A' : 'B'

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Loan A */}
        <div className={`rounded-xl shadow-sm border p-5 ${winner === 'A' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-700">Loan A</p>
            {winner === 'A' && <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Better deal</span>}
          </div>
          <MetricRow label="Total Cost" value={resultA.totalCost} prefix="$" />
          <MetricRow label="Effective Rate" value={resultA.effectiveAnnualRate} suffix="%" />
          <MetricRow label="Monthly Cost" value={resultA.monthlyCost} prefix="$" />
        </div>

        {/* Loan B */}
        <div className={`rounded-xl shadow-sm border p-5 ${winner === 'B' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-700">Loan B</p>
            {winner === 'B' && <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Better deal</span>}
          </div>
          <MetricRow label="Total Cost" value={resultB.totalCost} prefix="$" />
          <MetricRow label="Effective Rate" value={resultB.effectiveAnnualRate} suffix="%" />
          <MetricRow label="Monthly Cost" value={resultB.monthlyCost} prefix="$" />
        </div>
      </div>

      {/* Savings callout */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
        <p className="text-sm font-semibold text-emerald-800">
          You save ${savings.toLocaleString('en-US', { minimumFractionDigits: 2 })} with Loan {winner}
        </p>
      </div>
    </div>
  )
}

function MetricRow({ label, value, prefix = '', suffix = '' }: { label: string; value: number; prefix?: string; suffix?: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-900 font-mono">
        {prefix}{value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{suffix}
      </span>
    </div>
  )
}

function CostBreakdown({ label, breakdown, totalCost }: { label: string; breakdown: CostBreakdownItem[]; totalCost: number }) {
  if (breakdown.length === 0) return null

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{label}</h3>

      {/* Stacked bar */}
      <div className="h-8 rounded-lg overflow-hidden flex mb-4">
        {breakdown.map((item) => (
          <div
            key={item.label}
            style={{ width: `${item.percentage}%`, backgroundColor: item.color }}
            className="h-full transition-all duration-300"
            title={`${item.label}: $${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} (${item.percentage}%)`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="space-y-2">
        {breakdown.map((item) => (
          <div key={item.label} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-gray-600">{item.label}</span>
            </div>
            <span className="font-semibold text-gray-900 font-mono">
              ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              <span className="text-xs text-gray-400 ml-1">({item.percentage}%)</span>
            </span>
          </div>
        ))}

        <div className="pt-2 mt-2 border-t border-gray-100 flex items-center justify-between text-sm">
          <span className="font-semibold text-gray-700">Total</span>
          <span className="font-bold text-gray-900 font-mono">
            ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </div>
  )
}

function InputField({
  label, prefix, suffix, hint, ...props
}: {
  label: string
  prefix?: string
  suffix?: string
  hint?: string
  type: string
  value: string | number
  onChange: (val: string | number) => void
  step?: string
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>
        )}
        <input
          type={props.type}
          step={props.step}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          className={`w-full py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
            prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-8' : 'px-3'
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{suffix}</span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  )
}
